import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
    X,
    Play,
    Calendar as CalendarIcon,
    DollarSign,
    Settings2,
    AlertCircle
} from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { payloadAPI } from '@/lib/payload';
import { toast } from 'sonner';

interface BacktestModalProps {
    algorithm: any;
    onClose: () => void;
}

export function BacktestModal({ algorithm, onClose }: BacktestModalProps) {
    const queryClient = useQueryClient();
    const [formData, setFormData] = useState({
        name: `${algorithm.name} Backtest - ${new Date().toLocaleDateString()}`,
        initialCapital: 10000,
        startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0],
        commission: 0.1,
        slippage: 2,
    });

    const runBacktestMutation = useMutation({
        mutationFn: (data: any) => payloadAPI.runBacktest({
            algorithm: algorithm.id,
            timeframe: {
                startDate: data.startDate,
                endDate: data.endDate,
            },
            parameters: {
                initialCapital: data.initialCapital,
                commission: data.commission,
                slippage: data.slippage,
            }
        }),
        onSuccess: () => {
            toast.success('Backtest started successfully');
            queryClient.invalidateQueries({ queryKey: ['algorithm-backtests', algorithm.id] });
            onClose();
        },
        onError: (error: any) => {
            toast.error(error.message || 'Failed to start backtest');
        }
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        runBacktestMutation.mutate(formData);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <div className="bg-card w-full max-w-lg rounded-xl border border-border shadow-2xl relative animate-slide-up">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-primary/10 rounded-lg">
                            <Settings2 className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold">Configure Backtest</h2>
                            <p className="text-sm text-muted-foreground">{algorithm.name}</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-muted rounded-full transition-colors"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit}>
                    <div className="p-6 space-y-6">
                        <div className="space-y-4">
                            <div>
                                <label className="text-sm font-medium mb-1 block">Backtest Name</label>
                                <input
                                    type="text"
                                    required
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    className="w-full bg-background border px-4 py-2 rounded-md focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm font-medium mb-1 block">Start Date</label>
                                    <div className="relative">
                                        <CalendarIcon className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                                        <input
                                            type="date"
                                            required
                                            value={formData.startDate}
                                            onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                                            className="w-full bg-background border pl-10 pr-4 py-2 rounded-md focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="text-sm font-medium mb-1 block">End Date</label>
                                    <div className="relative">
                                        <CalendarIcon className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                                        <input
                                            type="date"
                                            required
                                            value={formData.endDate}
                                            onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                                            className="w-full bg-background border pl-10 pr-4 py-2 rounded-md focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label className="text-sm font-medium mb-1 block">Initial Capital (USD)</label>
                                <div className="relative">
                                    <DollarSign className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                                    <input
                                        type="number"
                                        required
                                        min="100"
                                        value={formData.initialCapital}
                                        onChange={(e) => setFormData({ ...formData, initialCapital: parseInt(e.target.value) })}
                                        className="w-full bg-background border pl-10 pr-4 py-2 rounded-md focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm font-medium mb-1 block">Commission (%)</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        required
                                        value={formData.commission}
                                        onChange={(e) => setFormData({ ...formData, commission: parseFloat(e.target.value) })}
                                        className="w-full bg-background border px-4 py-2 rounded-md focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                                    />
                                </div>
                                <div>
                                    <label className="text-sm font-medium mb-1 block">Slippage (pips)</label>
                                    <input
                                        type="number"
                                        required
                                        value={formData.slippage}
                                        onChange={(e) => setFormData({ ...formData, slippage: parseInt(e.target.value) })}
                                        className="w-full bg-background border px-4 py-2 rounded-md focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="bg-blue-500/5 border border-blue-500/20 p-4 rounded-lg flex gap-3">
                            <AlertCircle className="h-5 w-5 text-blue-500 shrink-0" />
                            <p className="text-xs text-blue-700 leading-relaxed">
                                Backtests are processed in a secure sandboxed environment on Cloudflare Workers.
                                Average processing time is 5-10 seconds depending on date range and data density.
                            </p>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="p-6 border-t flex gap-3 justify-end">
                        <Button variant="outline" type="button" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={runBacktestMutation.isPending}
                            className="bg-primary hover:bg-primary/90"
                        >
                            {runBacktestMutation.isPending ? (
                                <>Starting...</>
                            ) : (
                                <>
                                    <Play className="h-4 w-4 mr-2" />
                                    Run Backtest
                                </>
                            )}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
}
